import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router';

const PREFIX = 'search-scroll:';

const keyFor = (pathname: string, search: string) => `${PREFIX}${pathname}${search}`;

const read = (key: string): number | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw === null ? null : Number(raw);
  } catch {
    return null;
  }
};

const write = (key: string, value: number) => {
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    /* private mode or quota: restoration is a convenience, never required */
  }
};

/**
 * Whether a position is waiting to be restored for this exact URL. The global
 * ScrollToTop asks before resetting on a route change, so Back to a paged
 * search is not scrolled to the top underneath the restore.
 */
export const hasPendingScrollRestore = (pathname: string, search: string): boolean =>
  read(keyFor(pathname, search)) !== null;

/** Drop every saved position; a new search means none of them apply any more. */
export const clearScrollRestore = () => {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
};

/**
 * Scroll restoration for a paged results URL (Asana 1219353862024235,
 * Decision 10). The position is keyed by the exact URL (path + query, so by
 * page as well), written when the page unmounts or the tab is hidden, and read
 * back once after the results for that URL have rendered, then forgotten.
 * Coming back from a card page or an offer flow lands on the same page at the
 * same scroll; a fresh visit to the same URL later starts at the top.
 *
 * @param ready true once the rows for the current URL are on screen
 * @returns whether a saved position was applied for this URL (so the caller
 *          can skip its own scroll-to-top on that render)
 */
export const useScrollRestore = (ready: boolean): { restoredRef: React.MutableRefObject<boolean> } => {
  const { pathname, search } = useLocation();
  const key = keyFor(pathname, search);
  const keyRef = useRef(key);
  keyRef.current = key;
  const restoredRef = useRef(false);
  const appliedForRef = useRef<string | null>(null);

  // Save on the way out: unmount, navigation away (the key is the URL at that
  // moment) and page hide (bfcache, tab close). The position is tracked on
  // scroll rather than read in the cleanup: by the time an unmount cleanup
  // runs the old page's DOM is gone, the document is short, and scrollY is 0.
  const lastYRef = useRef(typeof window === 'undefined' ? 0 : window.scrollY);
  // Passive cleanups are deferred past paint, so the browser has already
  // clamped the shortened document to the top and fired a scroll event by the
  // time the save runs. The layout cleanup runs synchronously in the commit,
  // before that, and freezes the tracked value.
  const aliveRef = useRef(true);
  useLayoutEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  useEffect(() => {
    const track = () => {
      if (aliveRef.current) lastYRef.current = window.scrollY;
    };
    const save = () => write(keyRef.current, lastYRef.current);
    track();
    window.addEventListener('scroll', track, { passive: true });
    window.addEventListener('pagehide', save);
    return () => {
      window.removeEventListener('scroll', track);
      window.removeEventListener('pagehide', save);
      save();
    };
  }, []);

  // Restore once per URL, only after the results for it exist.
  useEffect(() => {
    if (!ready || appliedForRef.current === key) return;
    appliedForRef.current = key;
    const saved = read(key);
    restoredRef.current = saved !== null;
    if (saved === null) return;
    window.scrollTo({ top: saved });
    // Forget the key a tick later, not now: this effect runs before the route
    // tree's ScrollToTop effect in the same commit, and that one checks the key
    // to know it must stand down.
    window.setTimeout(() => {
      try {
        sessionStorage.removeItem(key);
      } catch {
        /* ignore */
      }
    }, 0);
  }, [ready, key]);

  return { restoredRef };
};

export default useScrollRestore;
